/**
 * Générateur — Automatisme : savoir que prendre la fraction d'un nombre
 * revient à multiplier la fraction par ce nombre.
 * Attendu ID : 1100 | Niveau : 4ème | Domaine : Nombres rationnels
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 9, ligne 657 :
 * « Savoir que prendre la fraction d'un nombre revient à multiplier la
 * fraction par ce nombre. »).
 *
 * ── Cadrage (sous-agent programme-maths, brouillon 2026-09-12) ─────────────
 * 1055 (5e, « le 1/3 de 18 ») et 1014 (6e, « les 2/3 de 12 ») LUS SUR
 * L'ÉCRITURE MULTIPLICATIVE : « les 2/3 de 12 » = « 2/3 × 12 » = « 12 × 2/3 »
 * = 8. 1055 excluait explicitement « 1/n × N (écriture de 4e) » : c'est le
 * neuf, l'identification des écritures et le calcul direct par « je
 * partage, je prends ». Le calcul reste dans les tables.
 * Frontières : fraction quelconque d'un nombre quelconque, fraction d'une
 * fraction (objectif l. 664) ; produit de deux fractions (l. 663) ; le
 * registre % (1056). Jamais 7,5, jamais de fraction > 1, nombre nu.
 * MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 7NB1 « fraction
 * de quantité » (dénominateurs 2-5, unités) ; une option non retenue écrit
 * « a/d × N » et « N × a/d », la forme de 4e reprise ici.
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : quatre lignes KaTeX,
 *     un entier à saisir, les trois écritures mêlées : 2/3 × 12 = … ;
 *     20 × 3/4 = … ; les 3/5 de 30 = … ; 1/4 × 28 = … (une fraction unitaire
 *     par série : choix mode auto, avec (3 ; 18) de 1055 importé et
 *     sacrifié, À ARBITRER). Quatre résultats distincts.
 *   Carte 2 « Quel calcul ? » (calcul_qcm_unique, nom de 1095) : « Les 3/4
 *     de 20, c'est… », QUATRE écritures : 3/4 × 20 (clé), 20 × 4/3
 *     (inversion), 20 ÷ 3 (diviser_par_numerateur), 20 − 3/4 (soustraire).
 *     « 20 × 3/4 » et « 20 ÷ 4 × 3 » sont JUSTES, jamais en distracteur.
 *     Pur rappel (doctrine, règle 9) : pas de fiche sur cette carte.
 *   Carte 3 « Vrai ou faux » (sens_tableau_vf) : trois lignes parmi sept
 *     gabarits sur les écritures et la valeur (« les 2/3 de 12 = 2/3 × 12 »
 *     V ; « 12 × 2/3 = 12 ÷ 2 × 3 » F ; « 2/3 × 12 = 12 ÷ 3 » F…), chaque
 *     ligne étiquetée par sa VALEUR (assert), une ou deux vraies.
 *
 * INVARIANTS : a/d avec d de 2 à 9, a de 1 à d − 1, irréductible ; N = d × q,
 * q de 2 à 10, q ≠ d (le nombre recopié serait la réponse) ; résultat
 * a × q ≤ 80, entier, unique ; tout en entiers.
 * SACRIFIÉS (fiches d'aide) : (2/3 ; 12), l'exemple de 6e (l. 1008) et de la
 * fiche à venir, sous ses trois écritures ; (1/3 ; 18) importé de 1055.
 * Auto-testés sur le texte rendu.
 *
 * ── Erreurs (mêmes noms qu'en 5e/6e) ─────────────────────────────────────
 *   mauvais_diviseur       : N ÷ m, m ≠ d (DEPP 4e 2023 q.6 : tiers de 60 →
 *                            15, 15,6 %) ;
 *   diviser_par_numerateur : N ÷ a × d (2/3 de 12 → 18), cible de 1014 ;
 *   part_seule             : N ÷ d (une part, pas a parts) ;
 *   fois_numerateur        : a × N ;
 *   soustraire             : N − a/d (1055, « prendre » lu « enlever ») ;
 *   inversion              : N × d/a (au QCM d'écriture).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
    retirerCodesErreur,
} from '../../../utils';
import { SACRIFIE as SACRIFIE_1055 } from '../../5eme/nombres-rationnels/gen_1055';

const ATTENDU_ID = 1100;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = 'Quel calcul ?';
const L_VF = 'Vrai ou faux';

const D_MIN = 2;
const D_MAX = 9;
const Q_MIN = 2;
const Q_MAX = 10;
const RES_MAX = 80;

// ─── Le domaine ───────────────────────────────────────────────────────────

/** Les exemples des fiches : « les 2/3 de 12 » (6e, fiche à venir) et « le 1/3 de 18 » (1055, importé). */
export const SACRIFIES = [{ a: 2, d: 3, N: 12 }, { a: 1, d: SACRIFIE_1055.n, N: SACRIFIE_1055.N }];
const estSacrifie = (a, d, N) => SACRIFIES.some((s) => s.a === a && s.d === d && s.N === N);

const gcd = (x, y) => (y === 0 ? x : gcd(y, x % y));

/** Un item { a, d, q, N, res } : a/d de N = a × q (assert). */
const verifier = (a, d, q) => {
    if (!Number.isInteger(a) || !Number.isInteger(d) || !Number.isInteger(q) || d < D_MIN || d > D_MAX || a < 1 || a >= d || gcd(a, d) !== 1 || q < Q_MIN || q > Q_MAX || q === d) {
        throw new Error(`gen_1100 : hors domaine ${a}/${d} de ${d * q}`);
    }
    const N = d * q;
    const res = a * q;
    if (res > RES_MAX || (a * N) % d !== 0 || (a * N) / d !== res) throw new Error('gen_1100 : arithmétique');
    if (estSacrifie(a, d, N)) throw new Error('gen_1100 : item sacrifié tiré');
    return { a, d, q, N, res };
};

/** `unitaire` : a = 1 ; `nonUnitaire` : a ≥ 2. */
const tirer = ({ unitaire = null } = {}) => {
    for (let essai = 0; essai < 120; essai++) {
        const d = randInt(D_MIN, D_MAX);
        const a = unitaire === true ? 1 : randInt(unitaire === false ? 2 : 1, d - 1);
        const q = randInt(Q_MIN, Q_MAX);
        if (a >= d || gcd(a, d) !== 1 || q === d || a * q > RES_MAX || estSacrifie(a, d, d * q)) continue;
        return verifier(a, d, q);
    }
    return verifier(unitaire === true ? 1 : 3, 4, 5);
};

const cle = (it) => `${it.a}/${it.d}/${it.N}`;

// ─── Écritures (KaTeX) ────────────────────────────────────────────────────

const fracTex = (a, d) => `\\dfrac{${a}}{${d}}`;
/** « les » → « Les » en tête de phrase (QCM). */
const majuscule = (e) => e.replace(/^\\text\{le(s?) \}/, '\\text{Le$1 }');

const ECRITURES = {
    fraction_fois: (it) => `${fracTex(it.a, it.d)} \\times ${it.N}`,
    fois_fraction: (it) => `${it.N} \\times ${fracTex(it.a, it.d)}`,
    les_de: (it) => `\\text{${it.a === 1 ? 'le' : 'les'} } ${fracTex(it.a, it.d)} \\text{ de } ${it.N}`,
};

/**
 * Valeur exacte [num, den] d'une écriture affichée (le second chemin de
 * l'assert) : produit, « les a/d de N », « N ÷ x × y », « a × N », entier.
 */
const valeur = (ecriture) => {
    const s = ecriture.replace(/\\text\{[^}]*\}/g, '').replace(/\s+/g, ' ').trim();
    let m = s.match(/^\\dfrac\{(\d+)\}\{(\d+)\} \\times (\d+)$/) || s.match(/^(\d+) \\times \\dfrac\{(\d+)\}\{(\d+)\}$/);
    if (m) {
        const [a, d, N] = s.startsWith('\\dfrac') ? [+m[1], +m[2], +m[3]] : [+m[2], +m[3], +m[1]];
        return [a * N, d];
    }
    m = s.match(/^\\dfrac\{(\d+)\}\{(\d+)\} (\d+)$/); // « les a/d de N » une fois les \text retirés
    if (m) return [+m[1] * +m[3], +m[2]];
    m = s.match(/^(\d+) \\div (\d+) \\times (\d+)$/);
    if (m) return [+m[1] * +m[3], +m[2]];
    m = s.match(/^(\d+) \\div (\d+)$/);
    if (m) return [+m[1], +m[2]];
    m = s.match(/^(\d+) \\times (\d+)$/);
    if (m) return [+m[1] * +m[2], 1];
    m = s.match(/^(\d+) - \\dfrac\{(\d+)\}\{(\d+)\}$/);
    if (m) return [+m[1] * +m[3] - +m[2], +m[3]];
    m = s.match(/^\d+$/);
    if (m) return [+s, 1];
    throw new Error(`gen_1100 : écriture illisible ${ecriture}`);
};
const memeValeur = (u, v) => u[0] * v[1] === v[0] * u[1];

const EXPLICATIONS = {
    diviser_par_numerateur: 'Prendre les 2/3 de 12, c\'est partager 12 en 3 (le dénominateur) et prendre 2 parts : 12 ÷ 3 × 2.',
    part_seule: '12 ÷ 3, c\'est UNE part : les 2/3, c\'est 2 parts.',
    fois_numerateur: 'On ne multiplie pas par le numérateur seul : 2/3 × 12, c\'est 12 partagé en 3, deux parts.',
    soustraire: '« Prendre les 2/3 de 12 », ce n\'est pas enlever 2/3 : c\'est multiplier 2/3 par 12.',
    inversion: 'La fraction se multiplie telle quelle : 3/4 × 20, pas 4/3.',
    mauvais_diviseur: 'On partage par le dénominateur : 12 en 3 parts, pas en 4.',
    regle: 'Prendre les 2/3 de 12, c\'est calculer 2/3 × 12 : 12 partagé en 3, deux parts, 8.',
};

// ─── Codes d'erreur (contenu.erreurs, codes communs du catalogue) ─────────

/** Le code commun d'un code du gen ou de l'atelier ; soustraire est neuf, mauvais_diviseur (erreur de calcul) reste sans code. */
const CODE_COMMUN = {
    inversion: 'inversion', diviser_par_numerateur: 'diviser_par_numerateur', part_seule: 'part_seule', fois_numerateur: 'fois_numerateur',
    soustraire: 'fraction_ajoutee_au_nombre', addition_confusion: 'fraction_ajoutee_au_nombre',
};

/** L'écriture décimale de p/q (virgule, trois décimales au plus) ; null si elle ne tombe pas juste. */
const decimal = (p, q) => {
    for (let k = 0; k <= 3; k++) {
        const n = (p * 10 ** k) / q;
        if (Number.isInteger(n)) {
            const s = String(n).padStart(k + 1, '0');
            return k ? `${s.slice(0, -k)},${s.slice(-k)}` : s;
        }
    }
    return null;
};

/** Les erreurs d'un trou « les a/d de N = … » (formules du reconnaisseur de l'atelier) : une valeur que deux familles se disputent n'a pas de code. */
const erreursCalcul = ({ a, d, N, res }, trou) => {
    const cands = [
        [N * d, a, 'diviser_par_numerateur'],
        ...(a >= 2 ? [[N, a, 'diviser_par_numerateur'], [N, d, 'part_seule']] : []),
        [a * N, 1, 'fois_numerateur'],
        [N * d + a, d, 'addition_confusion'],
        [N * d - a, d, 'soustraire'],
        ...[d - 1, d + 1].filter((m) => m >= 2).map((m) => [N * a, m, 'mauvais_diviseur']),
    ];
    const meme = (u, v) => u[0] * v[1] === v[0] * u[1];
    return cands
        .filter((u, i) => CODE_COMMUN[u[2]] && !meme(u, [res, 1]) && cands.findIndex((v) => meme(u, v)) === i
            && cands.every((v) => !meme(u, v) || CODE_COMMUN[v[2]] === CODE_COMMUN[u[2]]))
        .map(([p, q, code]) => ({ code: CODE_COMMUN[code], trou, valeur: decimal(p, q) }))
        .filter((e) => e.valeur !== null);
};

// ─── Carte 1 : Série flash (4 lignes saisie, un entier) ───────────────────

const CONSIGNE_FLASH = '<p><b>Calcule.</b></p>';

const lignesFlash = ([i1, i2, i3, i4]) => [
    `$${ECRITURES.fraction_fois(i1)} = {{${i1.res}}}$`,
    `$${ECRITURES.fois_fraction(i2)} = {{${i2.res}}}$`,
    `$${ECRITURES.les_de(i3)} = {{${i3.res}}}$`,
    `$${ECRITURES.fraction_fois(i4)} = {{${i4.res}}}$`,
];

const assemblerFlash = (items) => {
    const lignes = lignesFlash(items);
    lignes.forEach((l, i) => {
        const g = l.replace(/[$]/g, '').split(' = ')[0];
        if (!memeValeur(valeur(g), [items[i].res, 1])) throw new Error(`gen_1100 : ligne fausse ${l}`);
    });
    const payload = makeTexteTrou(lignes.join('\n'), CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie' });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items };
    payload.contenu = { ...payload.contenu, erreurs: items.flatMap((it, trou) => erreursCalcul(it, trou)) };
    return payload;
};

const genFlash = () => {
    for (let essai = 0; essai < 80; essai++) {
        const items = [tirer({ unitaire: false }), tirer({ unitaire: false }), tirer({ unitaire: false }), tirer({ unitaire: true })];
        if (new Set(items.map(cle)).size < 4 || new Set(items.map((it) => it.res)).size < 4) continue;
        return assemblerFlash(items);
    }
    return assemblerFlash([verifier(3, 4, 5), verifier(2, 5, 4), verifier(3, 5, 6), verifier(1, 4, 7)]);
};

// ─── Carte 2 : QCM « Quel calcul ? » ──────────────────────────────────────

const genQcm = () => {
    for (let essai = 0; essai < 80; essai++) {
        const it = tirer({ unitaire: false });
        const bon = `$${ECRITURES.fraction_fois(it)}$`;
        const faux = [
            { texte: `$${it.N} \\times ${fracTex(it.d, it.a)}$`, code: 'inversion' },
            { texte: `$${it.N} \\div ${it.a}$`, code: 'diviser_par_numerateur' },
            { texte: `$${it.N} - ${fracTex(it.a, it.d)}$`, code: 'soustraire' },
        ];
        // chaque distracteur faux EN VALEUR, valeurs deux à deux distinctes
        const vals = [bon, ...faux.map((f) => f.texte)].map((t) => valeur(t.replace(/[$]/g, '')));
        if (vals.some((u, i) => vals.some((v, j) => j > i && memeValeur(u, v)))) continue;
        const payload = makeQcm([bon, ...faux.map((f) => f.texte)], bon, `<p><b>${tex(majuscule(ECRITURES.les_de(it)))}, c'est…</b></p>`, opts(L_QCM));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { ...it, distracteurs: faux.map(({ code }) => ({ code })) };
        payload.contenu = { ...payload.contenu, erreurs: faux.map((f) => ({ code: CODE_COMMUN[f.code], valeur: f.texte })) };
        return payload;
    }
    const it = verifier(3, 4, 5);
    const bon = `$${ECRITURES.fraction_fois(it)}$`;
    const payload = makeQcm([bon, `$20 \\times ${fracTex(4, 3)}$`, '$20 \\div 3$', `$20 - ${fracTex(3, 4)}$`], bon, `<p><b>${tex(majuscule(ECRITURES.les_de(it)))}, c'est…</b></p>`, opts(L_QCM));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...it, distracteurs: [{ code: 'inversion' }, { code: 'diviser_par_numerateur' }, { code: 'soustraire' }] };
    const fauxRepli = [[`$20 \\times ${fracTex(4, 3)}$`, 'inversion'], ['$20 \\div 3$', 'diviser_par_numerateur'], [`$20 - ${fracTex(3, 4)}$`, 'soustraire']];
    payload.contenu = { ...payload.contenu, erreurs: fauxRepli.map(([valeur, code]) => ({ code: CODE_COMMUN[code], valeur })) };
    return payload;
};

// ─── Carte 3 : Vrai ou faux (3 lignes, étiquetées par leur valeur) ────────

/** Gabarits : { gauche, droite, vrai, code? } en KaTeX ; `a2` : exige a ≥ 2. */
const GABARITS = {
    les_de_egal_produit: (it) => ({ gauche: ECRITURES.les_de(it), droite: ECRITURES.fraction_fois(it), vrai: true }),
    commutee: (it) => ({ gauche: ECRITURES.fraction_fois(it), droite: ECRITURES.fois_fraction(it), vrai: true }),
    valeur: (it) => ({ gauche: ECRITURES[randomChoice(['fraction_fois', 'fois_fraction'])](it), droite: String(it.res), vrai: true }),
    partage_puis: (it) => ({ gauche: ECRITURES.les_de(it), droite: `${it.N} \\div ${it.d} \\times ${it.a}`, vrai: true, a2: true }),
    diviser_par_numerateur: (it) => ({ gauche: ECRITURES.fois_fraction(it), droite: `${it.N} \\div ${it.a} \\times ${it.d}`, vrai: false, code: 'diviser_par_numerateur', a2: true }),
    part_seule: (it) => ({ gauche: ECRITURES.fraction_fois(it), droite: `${it.N} \\div ${it.d}`, vrai: false, code: 'part_seule', a2: true }),
    fois_numerateur: (it) => ({ gauche: ECRITURES.les_de(it), droite: `${it.a} \\times ${it.N}`, vrai: false, code: 'fois_numerateur' }),
};

const genVf = () => {
    for (let essai = 0; essai < 80; essai++) {
        const noms = shuffle(Object.keys(GABARITS)).slice(0, 3);
        const items = noms.map((nom) => {
            const it = tirer({ unitaire: nom === 'fois_numerateur' ? null : false });
            const g = GABARITS[nom](it);
            return { nom, it, ...g, ligne: tex(`${g.gauche} = ${g.droite}`) };
        });
        if (new Set(items.map((x) => cle(x.it))).size < 3) continue;
        const nbVraies = items.filter((x) => x.vrai).length;
        if (nbVraies < 1 || nbVraies > 2) continue;
        items.forEach((x) => {
            if (memeValeur(valeur(x.gauche), valeur(x.droite)) !== x.vrai) throw new Error(`gen_1100 : ligne mal étiquetée ${x.ligne}`);
        });
        const payload = makeVraiFaux(items.map((x) => x.ligne), items.map((x) => (x.vrai ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items: items.map(({ nom, it, vrai, code }) => ({ nom, ...it, vrai, ...(code ? { code } : {}) })) };
        payload.contenu = { ...payload.contenu, erreurs: items.filter((x) => !x.vrai).map((x) => ({ code: CODE_COMMUN[x.code], ligne: x.ligne })) };
        return payload;
    }
    const its = [verifier(3, 4, 5), verifier(2, 5, 4), verifier(3, 5, 6)];
    const lignes = [
        tex(`${ECRITURES.les_de(its[0])} = ${ECRITURES.fraction_fois(its[0])}`),
        tex(`${ECRITURES.fois_fraction(its[1])} = 20 \\div 2 \\times 5`),
        tex(`${ECRITURES.fraction_fois(its[2])} = 30 \\div 5`),
    ];
    const payload = makeVraiFaux(lignes, [0, 1, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items: [{ nom: 'les_de_egal_produit', ...its[0], vrai: true }, { nom: 'diviser_par_numerateur', ...its[1], vrai: false, code: 'diviser_par_numerateur' }, { nom: 'part_seule', ...its[2], vrai: false, code: 'part_seule' }] };
    payload.contenu = { ...payload.contenu, erreurs: [{ code: CODE_COMMUN.diviser_par_numerateur, ligne: lignes[1] }, { code: CODE_COMMUN.part_seule, ligne: lignes[2] }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'la clé est une ÉCRITURE (a/d × N) ; les trois distracteurs sont faux EN VALEUR et de valeurs deux à deux distinctes (assert)',
        '« N × a/d » et « N ÷ d × a » sont des écritures JUSTES : jamais en distracteur',
        'sacrifiés : (2/3 ; 12) et (1/3 ; 18) importé de 1055, sous toutes les écritures',
    ],
    qcm_unique: {
        support: '« les a/d de N », a ≥ 2, N = d × q dans les tables, q ≠ d',
        familles: {
            inversion: 'N × d/a : la fraction retournée',
            diviser_par_numerateur: 'N ÷ a : le partage par le numérateur (2/3 de 12 → 18, cible de 1014)',
            soustraire: 'N − a/d : « prendre » lu « enlever » (décision 1055)',
        },
        note: 'pur rappel (doctrine des coups de pouce, règle 9) : pas de fiche sur cette carte ; mauvais_diviseur (DEPP 4e 2023 q.6, 15,6 %) est une erreur de CALCUL, elle vit à la série flash, pas au QCM d\'écriture',
    },
    vf_tableau_vf: 'sept gabarits (quatre vrais : les a/d de N = a/d × N, commutée, valeur, N ÷ d × a ; trois faux : N ÷ a × d, N ÷ d seul, a × N), chaque ligne étiquetée par sa valeur',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    calcul_qcm_unique: genQcm,
    sens_tableau_vf: genVf,
};

// etapes : nombre d'étapes de raisonnement de la carte, validé par le prof le
// 2026-10-06 (chantier arbres-4e) ; sert aux exercices complexes du groupe Acquis.
export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, etapes: 2, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'calcul', label: L_QCM, etapes: 1, formatsList: [{ key: 'calcul_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, etapes: 1, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre. Le choix reste proposé,
// il part sans nom.
const CODES_RETIRES = {
    calcul_qcm_unique: ['inversion', 'diviser_par_numerateur'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};
